/**
 * Bridge: OneTrust consent -> Shopify Customer Privacy API.
 *
 * Google Consent Mode (for the GTM tags) is driven by OneTrust's native
 * integration (see consent.js / consent-onetrust.liquid).
 *
 * This module forwards the OneTrust choice to Shopify via `setTrackingConsent`,
 * so the customer events pixel including the checkout pixel starts firing once 
 * the visitor grants consent.
 * It runs on the storefront only; the checkout pixel then reads the resulting Shopify
 * consent state itself.
 *
 * OneTrust group -> Shopify purpose mapping:
 *   C0002 Performance / Analytics -> analytics
 *   C0004 Targeting / Advertising -> marketing
 *   C0003 Functional              -> preferences
 *   C0001 (Strictly Necessary) is always on and maps to nothing.
 *   `sale_of_data` is intentionally not set here:
 *    Shopify honours GPC automatically and a data sale opt-out must be a customer-initiated flow.
 *    Add it only after legal sign off for the US markets in scope.
 */

import { hasConsentDecision } from '@theme/analytics-utils';

/** @returns {string[]} active OneTrust group ids, e.g. ['C0001','C0004'] */
function activeGroups() {
  const raw = window.OnetrustActiveGroups || '';
  return raw
    .split(',')
    .map((group) => group.trim())
    .filter(Boolean);
}

/** Map the active OneTrust groups to the Shopify consent payload. */
function consentFromGroups() {
  const groups = activeGroups();
  return {
    analytics: groups.includes('C0002'),
    marketing: groups.includes('C0004'),
    preferences: groups.includes('C0003'),
  };
}

/**
 * Remember the last payload so we only call Shopify when the choice changes.
 * Persisted so we don't re-record the same consent on every navigation (the
 * subscriptions below also fire on OneTrust SDK load).
 */
const SENT_KEY = 'jm:consent:sent';
let lastSent = '';
try {
  lastSent = sessionStorage.getItem(SENT_KEY) || '';
} catch (error) {
  /* storage unavailable */
}

/** Forward the current OneTrust choice to Shopify's Customer Privacy API. */
function sync() {
  const privacy = window.Shopify && window.Shopify.customerPrivacy;
  if (!privacy || typeof privacy.setTrackingConsent !== 'function') return;

  if (!hasConsentDecision()) return;

  const consent = consentFromGroups();
  const key = JSON.stringify(consent);
  if (key === lastSent) return; 
  lastSent = key;
  try {
    sessionStorage.setItem(SENT_KEY, key);
  } catch (error) {
    /* storage unavailable — non-fatal */
  }

  try {
    privacy.setTrackingConsent(consent, () => {});
  } catch (error) {
    // API not ready yet; a later ot:consent / OneTrustGroupsUpdated will retry.
    lastSent = '';
    try {
      sessionStorage.removeItem(SENT_KEY);
    } catch (removeError) {
      /* storage unavailable — non-fatal */
    }
  }
}

/** Load the Customer Privacy API, then sync and subscribe to consent changes. */
function init() {
  // Storefront only. On a store without OneTrust there is nothing to forward.
  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (error) return;
    // Subscribe first so no consent change is missed, then reflect any choice
    // already stored from a previous visit.
    window.addEventListener('ot:consent', sync);
    window.addEventListener('OneTrustGroupsUpdated', sync);
    sync();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
